/**
 * Image view component.
 * @module components/theme/View/Image360View
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Container } from 'semantic-ui-react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import prettybytes from 'pretty-bytes';
import { useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';
import ReactPannellum from 'react-pannellum';

import { flattenToAppURL } from '@plone/volto/helpers';

const messages = defineMessages({
  virtualTourAria: {
    id: 'VirtualTourAria',
    defaultMessage:
      'Premi TAB per abilitare i controlli del virtual-tour. Usa le frecce per spostarti a destra e sinistra e sopra/sotto. Tieni premuto il tasto SHIFT per fare lo zoom. Rilascia il tasto SHIFT per zoomare indietro.',
  },
});

/**
 * Image view component class.
 * @function Image360View
 * @params {object} content Content object.
 * @returns {string} Markup of the component.
 */
const Image360View = ({ content }) => {
  const intl = useIntl();
  const userId = useSelector((state) =>
    state.userSession.token ? jwtDecode(state.userSession.token).sub : null,
  );

  const config = {
    autoLoad: true,
    autoRotate: -2,
    //previewTitle: content.title,
    compass: true,
    showFullscreenCtrl: userId !== null,
  };

  useEffect(() => {
    document.querySelector('.image360wrapper').focus(); //focus on virtualtour on load
  }, [content.image.download]);

  return (
    <Container className="view-wrapper">
      <h1 className="documentFirstHeading">
        {content.title}
        {content.subtitle && ` - ${content.subtitle}`}
      </h1>
      {content.description && (
        <p className="documentDescription">{content.description}</p>
      )}
      <div
        className="image360wrapper"
        tabIndex={-1}
        role="region"
        aria-label={intl.formatMessage(messages.virtualTourAria)}
      >
        {content?.image?.download && (
          <>
            <ReactPannellum
              sceneId="firstScene"
              imageSource={flattenToAppURL(content.image.download)}
              config={config}
              className={`image-360-container ${!userId ? 'fullscreen' : ''}`}
              id="1"
            >
              {content.preview_caption && (
                <p className="documentCaption">{content.preview_caption}</p>
              )}
            </ReactPannellum>
            <a href={flattenToAppURL(content.image.download)}>
              <figcaption>
                <FormattedMessage
                  id="Size: {size}"
                  defaultMessage="Size: {size}"
                  values={{ size: prettybytes(content.image.size) }}
                />
                &nbsp; &mdash; &nbsp;
                <FormattedMessage
                  id="Click to download full sized image"
                  defaultMessage="Click to download full sized image"
                />
              </figcaption>
            </a>
          </>
        )}
      </div>
    </Container>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
Image360View.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.shape({
      scales: PropTypes.shape({
        preview: PropTypes.shape({
          download: PropTypes.string,
        }),
      }),
    }),
  }).isRequired,
};

export default Image360View;
